import React from "react";
import { fmt } from "./parts";

// Clean, print-ready "Nearfluence Business Analytics Report".
// Renders ONLY genuine available data — sections/metrics with no data are omitted.

function firstLast(series = []) {
  if (!series || series.length < 2) return null;
  return { from: series[0].value, to: series[series.length - 1].value };
}

function Stat({ label, value }) {
  return (
    <div style={{ border: "1px solid #e4e4e7", borderRadius: 12, padding: "12px 14px" }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#18181b" }}>{value}</div>
      <div style={{ fontSize: 12, color: "#71717a", marginTop: 2 }}>{label}</div>
    </div>
  );
}

export function BusinessReport({ data }) {
  const a = data.activity || {};
  const generated = new Date(data.generated_at || Date.now());
  const totalViews = (a.opportunity_views || 0) + (a.public_views || 0);

  // Only show activity metrics that carry genuine (non-zero) data.
  const activityStats = [
    { label: "Opportunities posted", value: a.opportunities_posted },
    { label: "Applications received", value: a.applications_received },
    { label: "Creators worked with", value: a.creators_worked },
    { label: "Collaborations completed", value: a.collaborations_completed },
    { label: "Campaigns run", value: a.campaigns_run },
    { label: "Total listing views", value: totalViews },
    { label: "Events hosted", value: a.events_hosted },
    { label: "Event creator reach", value: a.event_content_reach },
  ].filter((s) => (s.value || 0) > 0).map((s) => ({ ...s, value: fmt(s.value) }));

  const social = data.social || {};
  const socialConnected = social.connected && (social.platforms || []).length > 0;
  const perf = data.campaign_performance || [];
  const creators = (data.creators || []).filter((c) => c.collaborations > 0);

  const H2 = ({ children }) => (
    <h2 style={{ fontSize: 15, fontWeight: 800, color: "#18181b", margin: "26px 0 12px", borderBottom: "2px solid #059669", paddingBottom: 6 }}>{children}</h2>
  );

  return (
    <div className="nf-print-report" data-testid="business-report" style={{ fontFamily: "Arial, Helvetica, sans-serif", color: "#18181b", background: "#fff", padding: 8, maxWidth: 800, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e4e4e7", paddingBottom: 14 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 900 }}>Near<span style={{ color: "#059669" }}>fluence</span></div>
          <div style={{ fontSize: 13, color: "#71717a", marginTop: 2 }}>Business Analytics Report</div>
        </div>
        <div style={{ textAlign: "right", fontSize: 12, color: "#71717a" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#18181b" }}>{data.business_name}</div>
          <div>Last {data.period_days || 90} days</div>
          <div>Generated {generated.toLocaleDateString()}</div>
        </div>
      </div>

      {activityStats.length > 0 && (
        <>
          <H2>Activity summary</H2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
            {activityStats.map((s) => <Stat key={s.label} label={s.label} value={s.value} />)}
          </div>
        </>
      )}

      {socialConnected && (
        <>
          <H2>Social performance</H2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
            {social.platforms.map((p) => (
              <Stat key={p.platform} label={`@${p.username} · followers${p.engagement_rate != null ? ` · ${p.engagement_rate}% eng.` : ""}`} value={fmt(p.followers)} />
            ))}
          </div>
          {(() => {
            const f = firstLast(social.trends?.followers);
            const r = firstLast(social.trends?.reach);
            const rows = [];
            if (f) rows.push(`Followers: ${fmt(f.from)} → ${fmt(f.to)}`);
            if (r) rows.push(`Reach: ${fmt(r.from)} → ${fmt(r.to)}`);
            return rows.length ? <p style={{ fontSize: 13, color: "#3f3f46", marginTop: 10 }}>{rows.join("  ·  ")}</p> : null;
          })()}
        </>
      )}

      {socialConnected && perf.length > 0 && (
        <>
          <H2>Campaign performance</H2>
          {perf.map((c) => (
            <div key={c.campaign_id} style={{ border: "1px solid #e4e4e7", borderRadius: 12, padding: 14, marginBottom: 10 }}>
              <div style={{ fontWeight: 700 }}>{c.name} <span style={{ fontSize: 12, color: "#a1a1aa", fontWeight: 400 }}>({c.start_date} → {c.end_date})</span></div>
              <table style={{ width: "100%", marginTop: 8, fontSize: 13, borderCollapse: "collapse" }}>
                <thead><tr style={{ color: "#71717a", textAlign: "left" }}><th>Metric</th><th>Before</th><th>During</th><th>After</th></tr></thead>
                <tbody>
                  {[["Reach", c.reach], ["Followers", c.followers], ["Engagement", c.engagement, "%"]].filter(([, b]) => b && b.during != null).map(([label, b, sfx = ""]) => (
                    <tr key={label} style={{ borderTop: "1px solid #f4f4f5" }}>
                      <td style={{ padding: "4px 0", fontWeight: 600 }}>{label}</td>
                      <td>{b.before != null ? `${label === "Engagement" ? b.before : fmt(b.before)}${sfx}` : "—"}</td>
                      <td>{`${label === "Engagement" ? b.during : fmt(b.during)}${sfx}`}</td>
                      <td>{b.after != null ? `${label === "Engagement" ? b.after : fmt(b.after)}${sfx}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <p style={{ fontSize: 11, color: "#a1a1aa" }}>Changes observed during each campaign window — helpful signal, not guaranteed attribution.</p>
        </>
      )}

      {creators.length > 0 && (
        <>
          <H2>Creators &amp; events</H2>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead><tr style={{ color: "#71717a", textAlign: "left" }}><th style={{ padding: "4px 0" }}>Creator</th><th>Niche</th><th>Collaborations</th><th>Event reach</th></tr></thead>
            <tbody>
              {creators.map((c) => (
                <tr key={c.user_id} style={{ borderTop: "1px solid #f4f4f5" }}>
                  <td style={{ padding: "5px 0", fontWeight: 600 }}>{c.name}</td>
                  <td>{c.niche || "—"}</td>
                  <td>{c.collaborations}</td>
                  <td>{c.event_reach ? fmt(c.event_reach) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize: 11, color: "#a1a1aa", marginTop: 6 }}>Follower and event-reach figures are self-reported by creators until their accounts are connected via API.</p>
        </>
      )}

      <div style={{ marginTop: 28, paddingTop: 12, borderTop: "1px solid #e4e4e7", fontSize: 11, color: "#a1a1aa", textAlign: "center" }}>
        Nearfluence · Local creators. Local businesses. Real influence. · Report generated {generated.toLocaleString()}
      </div>
    </div>
  );
}
